/**
 * Default payment logs filters using the shared report toolbar + drawer pattern.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { ListFilterDrawer } from '@/components/ui/ListFilterDrawer'
import { ListSearchToolbar } from '@/components/ui/ListSearchToolbar'
import { SearchableSelect, type SearchableSelectOption } from '@/components/ui/SearchableSelect'
import { useAppSelector } from '@/app/hooks'
import { selectUser, selectIsAuthenticated } from '@/features/auth/authSlice'
import { formatDateForAPI } from '@/features/invitee/utils/inviteeFormatters'
import { getCompanies, getProperties } from '@/features/invitee/services/inviteeService'
import { ReportDateRangePicker } from './ReportDateRangePicker'
import type { DefaultPaymentLogsFilterValues } from '../types/filters'
import type { DefaultPaymentLogsInlineFilterBarProps } from '../types/components'
import {
    CLAIM_STATUS_SELECT_OPTIONS,
    PAYMENT_STATUS_FILTER_OPTIONS,
    YES_NO_FILTER_OPTIONS,
} from '../constants/filterOptions'
import { LABEL_CLASS } from '../constants/configuredReportStyles'

function FilterField({
    label,
    children,
}: {
    label: string
    children: ReactNode
}) {
    return (
        <div className="space-y-2">
            <label className={LABEL_CLASS}>{label}</label>
            <div className="min-w-0">{children}</div>
        </div>
    )
}

export function DefaultPaymentLogsInlineFilterBar({
    values,
    onFiltersChange,
    onReset,
    searchTerm,
    onSearchTermChange,
    isOpen,
    onOpen,
    onClose,
    activeFilterCount,
    actions,
    middle,
    toolbarClassName,
    initialPropertyOption,
}: DefaultPaymentLogsInlineFilterBarProps) {
    const user = useAppSelector(selectUser)
    const isAuthenticated = useAppSelector(selectIsAuthenticated)

    const [companies, setCompanies] = useState<SearchableSelectOption[]>([])
    const [loadingCompanies, setLoadingCompanies] = useState(false)
    const [properties, setProperties] = useState<SearchableSelectOption[]>([])
    const [loadingProperties, setLoadingProperties] = useState(false)

    const lockedCompanyId = useMemo(
        () => (user?.current_role?.company_id ? String(user.current_role.company_id) : undefined),
        [user],
    )
    const lockedPropertyId = useMemo(
        () => (user?.current_role?.property_id ? String(user.current_role.property_id) : undefined),
        [user],
    )

    const loadCompanies = async (search: string, orgId?: string) => {
        if (!isAuthenticated) return
        setLoadingCompanies(true)
        try {
            const data = await getCompanies(search, orgId)
            setCompanies(
                data.map((c) => ({
                    value: String(c.company_id),
                    label: c.company_name || String(c.company_id),
                })),
            )
        } catch (e) {
            console.error(e)
        } finally {
            setLoadingCompanies(false)
        }
    }

    const loadProperties = async (search: string, companyId?: string) => {
        if (!isAuthenticated) return
        setLoadingProperties(true)
        try {
            const data = await getProperties(search, companyId)
            setProperties(
                data.map((p) => ({
                    value: String(p.property_id),
                    label: p.property_name || String(p.property_id),
                })),
            )
        } catch (e) {
            console.error(e)
        } finally {
            setLoadingProperties(false)
        }
    }

    useEffect(() => {
        if (!isAuthenticated) return
        const locked: Partial<DefaultPaymentLogsFilterValues> = {}
        if (lockedCompanyId && values.company_id !== lockedCompanyId) locked.company_id = lockedCompanyId
        if (lockedPropertyId && values.property_id !== lockedPropertyId) locked.property_id = lockedPropertyId
        if (Object.keys(locked).length > 0) onFiltersChange(locked)
    }, [
        isAuthenticated,
        lockedCompanyId,
        lockedPropertyId,
        values.company_id,
        values.property_id,
        onFiltersChange,
    ])

    useEffect(() => {
        if (!isAuthenticated) return
        if (!lockedCompanyId && companies.length === 0) void loadCompanies('', undefined)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated])

    useEffect(() => {
        if (!isAuthenticated || lockedPropertyId) return
        const companyId = values.company_id || lockedCompanyId
        void loadProperties('', companyId)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, values.company_id, lockedCompanyId, lockedPropertyId])

    useEffect(() => {
        if (initialPropertyOption && initialPropertyOption.value) {
            setProperties((prev) => {
                const has = prev.some((o) => o.value === initialPropertyOption.value)
                if (has) return prev
                return [initialPropertyOption, ...prev]
            })
        }
    }, [initialPropertyOption])

    const vintageYearChoices = useMemo(() => {
        const y = new Date().getFullYear()
        return Array.from({ length: 4 }, (_, i) => y - i)
    }, [])

    const handleDateChange = (startDate: Date | null, endDate: Date | null) => {
        onFiltersChange({
            fromDt: startDate ? formatDateForAPI(startDate) : undefined,
            toDt: endDate ? formatDateForAPI(endDate) : undefined,
        })
    }

    const handleLeaseDateChange = (startDate: Date | null, endDate: Date | null) => {
        onFiltersChange({
            lease_start_fromDt: startDate ? formatDateForAPI(startDate) : undefined,
            lease_start_toDt: endDate ? formatDateForAPI(endDate) : undefined,
        })
    }

    const handleClaimStatusDateChange = (startDate: Date | null, endDate: Date | null) => {
        onFiltersChange({
            claim_status_fromDt: startDate ? formatDateForAPI(startDate) : undefined,
            claim_status_toDt: endDate ? formatDateForAPI(endDate) : undefined,
        })
    }

    return (
        <>
            <ListSearchToolbar
                searchValue={searchTerm}
                searchPlaceholder="Search.."
                onSearchChange={onSearchTermChange}
                onOpenFilters={onOpen}
                activeFilterCount={activeFilterCount}
                actions={actions}
                middle={middle}
                className={toolbarClassName}
                testIdPrefix="default-payment-logs"
            />

            <ListFilterDrawer
                isOpen={isOpen}
                onClose={onClose}
                onReset={onReset}
                testIdPrefix="default-payment-logs"
            >
                <FilterField label="Company">
                    <SearchableSelect
                        triggerDataTestId="default-payment-logs-filters-company-trigger"
                        placeholder="All Company"
                        searchPlaceholder="Search by company name, city, state"
                        options={
                            lockedCompanyId
                                ? [
                                      {
                                          value: lockedCompanyId,
                                          label: user?.current_role?.company_name || lockedCompanyId,
                                      },
                                  ]
                                : companies
                        }
                        value={lockedCompanyId || values.company_id || ''}
                        onChange={(val) => {
                            if (lockedCompanyId) return
                            const companyId = Array.isArray(val) ? val[0] : val
                            onFiltersChange({
                                company_id: companyId || undefined,
                                property_id: undefined,
                            })
                        }}
                        onSearch={lockedCompanyId ? undefined : (s) => loadCompanies(s, undefined)}
                        immediateSearchOnPaste
                        loading={loadingCompanies}
                        disabled={!!lockedCompanyId}
                    />
                </FilterField>

                <FilterField label="Property">
                    <SearchableSelect
                        triggerDataTestId="default-payment-logs-filters-property-trigger"
                        placeholder="All Properties"
                        searchPlaceholder="Search by property name, city, state"
                        options={
                            lockedPropertyId
                                ? [
                                      {
                                          value: lockedPropertyId,
                                          label:
                                              user?.current_role?.property_name || lockedPropertyId,
                                      },
                                  ]
                                : properties
                        }
                        value={lockedPropertyId || values.property_id || ''}
                        onChange={(val) => {
                            if (lockedPropertyId) return
                            const propertyId = Array.isArray(val) ? val[0] : val
                            onFiltersChange({ property_id: propertyId || undefined })
                        }}
                        onSearch={
                            lockedPropertyId
                                ? undefined
                                : (s) =>
                                      loadProperties(s, values.company_id || lockedCompanyId || undefined)
                        }
                        immediateSearchOnPaste
                        disabled={!!lockedPropertyId}
                        loading={loadingProperties}
                    />
                </FilterField>

                <FilterField label="Payee Verified">
                    <SearchableSelect
                        triggerDataTestId="default-payment-logs-filters-payee-verified"
                        placeholder="Payee Verified"
                        searchable={false}
                        options={YES_NO_FILTER_OPTIONS}
                        value={values.payment_verified ?? ''}
                        onChange={(val) => {
                            const v = Array.isArray(val) ? val[0] : val
                            onFiltersChange({
                                payment_verified:
                                    v === '0' || v === '1' ? v : undefined,
                            })
                        }}
                    />
                </FilterField>

                <FilterField label="Payment Status">
                    <SearchableSelect
                        triggerDataTestId="default-payment-logs-filters-payment-status-trigger"
                        multiple
                        placeholder="Payment Status"
                        options={PAYMENT_STATUS_FILTER_OPTIONS}
                        value={values.payment_status ?? []}
                        onChange={(val) => {
                            onFiltersChange({
                                payment_status: Array.isArray(val) ? val : [],
                            })
                        }}
                    />
                </FilterField>

                <FilterField label="Reimbursement Status">
                    <SearchableSelect
                        triggerDataTestId="default-payment-logs-filters-claim-status"
                        placeholder="All Reimbursement Statuses"
                        options={CLAIM_STATUS_SELECT_OPTIONS}
                        value={values.claim_status ?? ''}
                        onChange={(val) => {
                            const v = Array.isArray(val) ? val[0] : val
                            onFiltersChange({ claim_status: v || undefined })
                        }}
                    />
                </FilterField>

                <FilterField label="Payment Date">
                    <div data-testid="default-payment-logs-filters-date-wrap" className="relative">
                        <ReportDateRangePicker
                            startDate={values.fromDt}
                            endDate={values.toDt}
                            onChange={handleDateChange}
                            placeholder="Select Date Range"
                        />
                    </div>
                </FilterField>

                <FilterField label="Vintage Year">
                    <SearchableSelect
                        triggerDataTestId="default-payment-logs-filters-vintage-year"
                        placeholder="Vintage Year"
                        options={vintageYearChoices.map((year) => ({
                            value: String(year),
                            label: String(year),
                        }))}
                        value={values.vintage_year ?? ''}
                        onChange={(val) => {
                            const v = Array.isArray(val) ? val[0] : val
                            onFiltersChange({ vintage_year: v || undefined })
                        }}
                    />
                </FilterField>

                <FilterField label="Lease Start Date">
                    <div data-testid="default-payment-logs-filters-lease-date-wrap" className="relative">
                        <ReportDateRangePicker
                            startDate={values.lease_start_fromDt}
                            endDate={values.lease_start_toDt}
                            onChange={handleLeaseDateChange}
                            placeholder="Select Date Range"
                        />
                    </div>
                </FilterField>

                <FilterField label="Reimbursement Status Date">
                    <div data-testid="default-payment-logs-filters-claim-date-wrap" className="relative">
                        <ReportDateRangePicker
                            startDate={values.claim_status_fromDt}
                            endDate={values.claim_status_toDt}
                            onChange={handleClaimStatusDateChange}
                            placeholder="Select Date Range"
                        />
                    </div>
                </FilterField>

                <FilterField label="Re-Issued Payments">
                    <SearchableSelect
                        triggerDataTestId="default-payment-logs-filters-reissue"
                        placeholder="Select Option"
                        searchable={false}
                        options={YES_NO_FILTER_OPTIONS}
                        value={values.is_reissue_payment ?? ''}
                        onChange={(val) => {
                            const v = Array.isArray(val) ? val[0] : val
                            onFiltersChange({
                                is_reissue_payment:
                                    v === '0' || v === '1' ? v : undefined,
                            })
                        }}
                    />
                </FilterField>
            </ListFilterDrawer>
        </>
    )
}

export type { DefaultPaymentLogsFilterValues } from '../types/filters'
